import { Button, Card, CardContent, CardHeader, LinearProgress, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, TextField, Toolbar, Typography, makeStyles } from '@material-ui/core';
import { PageHeader } from 'pages/CustomerBookingCreate/components/PageHeader';
import { STEP_CREATE_ACCOUNT, STEP_LOCATION, STEP_PERSONAL_DETAILS, STEP_SCHEDULE, STEP_SERVICE_BREAKDOWN, STEP_SERVICE_CATEGORIES, STEP_SERVICE_DETAILS, STEP_SERVICE_PACKAGES } from 'pages/CustomerBookingCreate/constants';
import { bookingState, cartEmptyState, cartPackageItemsState, packagesState, postLoginStepState, servicesState, sessionTokenState, stepState, userState } from 'pages/CustomerBookingCreate/state';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import ReactJson from 'react-json-view';
import { useHistory } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { anonCalculate } from 'services/anon.service';
import { isAuthenticated, useUserState } from 'services/authentication.service';
import { calculate } from 'services/createBooking.services';
import { localizeCurrency } from 'util/localizeFormat';
import { useTrackEvent } from 'util/trackEvent';
import { InputAdornment } from "@material-ui/core";
import { applyPromoCode } from 'services/payments.service';
import { toasterror } from 'util/toasterror';

const useStyles = makeStyles(theme => ({
  discount: {
    color: theme.palette.success.dark,
  }
}));
const ServiceBreakdownScreen = ({ }) => {
  const classes = useStyles()

  const [step, setStep] = useRecoilState(stepState)
  const [postLoginStep, setPostLoginStep] = useRecoilState(postLoginStepState);

  const [loading, setLoading] = useState(false);
  const history = useHistory()
  const trackEvent = useTrackEvent()

  return (
    <>
      <PageHeader
        backStep={STEP_SERVICE_PACKAGES}
        currentStep={STEP_SERVICE_BREAKDOWN}
        title={"How much will it cost?"}
        subheader={"See details of cost breakdown below"}
        isCartEnabled={false}
        loading={loading}
      >
        <>
          <Card>
            <Breakdown />
          </Card>
          <Toolbar>
            <div style={{ flexGrow: 1 }} />
            <Button onClick={() => {
              if (isAuthenticated()) {
                trackEvent('Schedule - continue to personal details')
                setStep(STEP_PERSONAL_DETAILS)
                setPostLoginStep(STEP_PERSONAL_DETAILS)
                history.replace(STEP_PERSONAL_DETAILS, { internal: false, defer: true })
              } else {
                trackEvent('Schedule - continue to create account')
                setStep(STEP_CREATE_ACCOUNT)
                setPostLoginStep(STEP_PERSONAL_DETAILS)
                history.replace(STEP_CREATE_ACCOUNT, { internal: false, defer: true })
              }
              // setStep(STEP_SCHEDULE)
              // history.replace(STEP_SCHEDULE, { internal: true })
            }} color="primary" variant="contained" className={classes.button}>Continue</Button>
          </Toolbar>
        </>
      </PageHeader>
    </>
  )
}

const Breakdown = () => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false);
  const [session_token, setSession_token] = useRecoilState(sessionTokenState);
  const { getUser } = useUserState()
  const [breakDown, setBreakDown] = useState(null);
  const [booking, setBooking] = useRecoilState(bookingState);
  const [services, setServices] = useRecoilState(servicesState);
  const [packages, setPackages] = useRecoilState(packagesState);
  const isCartEmpty = useRecoilValue(cartEmptyState)

  const [promoCode, setPromoCode] = useState(null);
  const [promoValue, setPromoValue] = useState(null);
  const [promoCodeInputValue, setPromoCodeInputValue] = useState(null);


  const trackEvent = useTrackEvent()

  const doCalculate = useCallback(
    async ({ promo }) => {
      setLoading(true)
      try {
        let res = null
        let params = {
          latitude: booking?.location?.latitude,
          longitude: booking?.location?.longitude,
          extended_package_items: services?.map(x => x?.id),
          extended_packages: packages?.map(x => x?.id),
          packages: null,
          serviceCategoryTypeId: booking?.serviceCategory?.featured_service?.type_id || booking?.serviceCategory?.type?.id,
          code: promo?.code,
          code_type: promo?.type,
          "white_label_customer": booking?.white_label_customer?.id,

        }
        if (isAuthenticated()) {
          const user = await getUser()
          params = {
            ...params,
            clientId: user?.client?.id || user?.user?.id,
          }
          res = await calculate(params)
        } else {
          res = await anonCalculate({
            ...params,
            session_token: session_token,
          })
        }
        setBreakDown(res)
        setLoading(false)
      } catch (error) {
        toasterror(error?.message?.en || error?.response?.data?.message?.en || error?.response?.data?.details, { error })
        setLoading(false)
        trackEvent('Breakdown calculate - error', { error: error?.message?.en || error?.response?.data?.message?.en || error?.response?.data?.details })
      }
    },
    [booking, services, packages, getUser, promoValue],
  );

  const usePromoCode = useCallback(
    async (code) => {
      if (!code) return
      try {
        setLoading(true)
        const result = await applyPromoCode(code)
        setPromoValue(result)
        doCalculate({ promo: result })
        setBooking({ ...booking, promo: result })
      } catch (error) {
        setPromoValue(null)
        toasterror(error?.message?.en || error?.response?.data?.message?.en || error?.response?.data?.details)
        setPromoCode(null)
        setPromoCodeInputValue(null)

      } finally {
        setLoading(false)
      }
      return null
    }, [booking]
  );

  useEffect(
    () => {
      usePromoCode(promoCode)
    }, [promoCode]
  );

  useEffect(() => {
    if (!isCartEmpty) {
      doCalculate({ promo: booking?.promo })
    }
  }, [])

  useEffect(() => {
    if (booking?.promo) {
      setPromoCode(booking?.promo?.code)
      setPromoCodeInputValue(booking?.promo?.code)
      setPromoValue(booking?.promo)
    }
  }, [booking?.promo])

  const feeBreakdown = useMemo(
    () => {
      const fees = [
        { label: 'Base Service Charge', value: breakDown?.base },
        { label: 'Hazard Fee', value: breakDown?.hazard_fee },
        { label: "Transport Fee", value: breakDown?.transport_fee },
        { label: "Kit Fee", value: breakDown?.kit_fee },
        { label: "Product Fee", value: breakDown?.product_fee },
        { label: "PPE Fee", value: breakDown?.ppe_fee },
      ]

      const feeVisibleFees = fees.filter(x => x.value > 0)
      return feeVisibleFees.map((x, i) => (
        <TableRow key={i}>
          <TableCell>
            {x.label}
          </TableCell>
          <TableCell variant='head' width="150" align="right">
            {localizeCurrency(x.value)}
          </TableCell>
        </TableRow>
      ))
    }, [breakDown]
  );

  const discountBreakdown = useMemo(
    () => {

      const discounts = [
        { label: 'Discount', value: breakDown?.discount },
        { label: ' --- Promo Discount', value: breakDown?.discount_promo },
        { label: "Partner Covered", value: breakDown?.partner_covered },
        { label: "HMO", value: breakDown?.hmo }
      ]

      const discountVisibleFees = discounts.filter(x => x.value > 0)
      return discountVisibleFees.map((x, i) => (
        <TableRow key={i}>
          <TableCell>
            {x.label}
          </TableCell>
          <TableCell variant='head' width="150" align="right">
            <Typography className={classes.discount}>
              {(localizeCurrency(x.value))}
            </Typography>
          </TableCell>
        </TableRow>
      ))
    }, [breakDown]
  );

  const serviceBreakdown = useMemo(
    () => {
      const packageBreakdown = packages?.map((x, i) => (
        <TableRow key={`pkg-${i}`}>
          <TableCell>
            {x?.label} Package
          </TableCell>
          <TableCell variant='head' width="150" align="right">
            {localizeCurrency(x?.fee || x?.fee_service)}
          </TableCell>
        </TableRow>
      ))
      const svcBreakdown = services?.map((x, i) => (
        <TableRow key={`svc-${i}`}>
          <TableCell>
            {x?.label}
          </TableCell>
          <TableCell variant='head' width="150" align="right">
            {localizeCurrency(x?.fee || x?.fee_service)}
          </TableCell>
        </TableRow>
      ))
      return (
        <>
          {packageBreakdown}
          {svcBreakdown}
        </>
      )
    }, [services, packages]
  );


  if (loading) {
    return <LinearProgress />
  }
  return (
    <>
      <CardContent>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                variant='head'
                colSpan={2}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    Service Breakdown
                  </div>
                  {
                    isAuthenticated() && (
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Promo Code"
                        disabled={promoValue}
                        value={promoCodeInputValue}
                        onChange={(e) => {
                          trackEvent('Create Booking Service Breakdown - Promo Code Input')
                          setPromoCodeInputValue(e.target.value)
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                disabled={promoValue}
                                color="primary" onClick={() => {
                                  trackEvent('Create Booking Service Breakdown - Apply Promo Code')
                                  setPromoCode(promoCodeInputValue)
                                }}>Apply</Button>
                            </InputAdornment>
                          )
                        }}
                      />
                    )
                  }
                </div>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {serviceBreakdown}
            <TableRow>
              <TableCell variant='head'>Service Charges</TableCell>
              <TableCell></TableCell>
            </TableRow>
            {feeBreakdown}
            {discountBreakdown}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TableCell variant='head'>TOTAL</TableCell>
              <TableCell variant='head' width="150" align="right">
                {localizeCurrency(breakDown?.total)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </CardContent>
    </>
  )
}

export { ServiceBreakdownScreen, Breakdown }